import { BaseCell, ChangeCell, type ITableProps } from "@similarweb/pro-logic-components";

import { TrendCell } from "app/components/React/Table/cells";
import { translate } from "app/filters/i18nFilter";
import { BenchmarkMetricName } from "app/pages/benchmark-tracker/types/api.types";

import { formatChangeValue } from "../../../../helpers/formatChangeValue";
import { ColorDomainCellWithLink } from "../../../Share/ColorDomainCellWithLink/ColorDomainCellWithLink";
import WebsiteWithFaviconCell from "../../../Share/WebsiteWithFaviconCell/WebsiteWithFaviconCell";

import { ChangeRankCell } from "./cells/ChangeRankCell";
import { CustomPercentageCell } from "./cells/CustomPercentageCell";
import { StyledCellContainer, TrendContainer } from "./styles";
import type { ColumnNames, ParentColumnNames } from "./type";

import "./styles.css";

export const NUMBER_OF_ELEMENTS = 5;
const RANK_SYMBOL = "#";

type AntColumnType<T extends object = any> = ITableProps<T>["columnConfig"][number] & {
    groupName?: string;
    visible?: boolean;
    children?: AntColumnType[];
};

export type BenchmarkColumnSchema = AntColumnType & {
    metricName?: BenchmarkMetricName;
    width?: string | number;
    onOtherCompetitorsClick?: () => void;
};

export const customPercentageCellRender = (value: number, row: Record<PropertyKey, any>) => (
    <CustomPercentageCell
        value={value}
        row={row}
    />
);

const defaultConfig: Partial<BenchmarkColumnSchema> = {
    visible: true,
    width: 100,
    resize: false,
};

const defaultPercentageConfig: Partial<BenchmarkColumnSchema> = {
    ...defaultConfig,
    render: customPercentageCellRender,
};

const getDefaultColumns = (): Record<ColumnNames, Partial<BenchmarkColumnSchema>> => ({
    domain: {
        ...defaultConfig,
        key: "domain",
        dataIndex: "domain",
        width: 400,
        displayName: translate("common.domain"),
        title: translate("common.domain"),
        render: (value: string, row: Record<PropertyKey, any>) => (
            <WebsiteWithFaviconCell
                value={value}
                row={row}
                tooltipClassName="websiteTooltipModalView"
            />
        ),
    },
    colorDomain: {
        ...defaultConfig,
        key: "colorDomain",
        dataIndex: "domain",
        width: 400,
        displayName: translate("common.domain"),
        title: translate("common.domain"),
        render: (value: string, row: Record<PropertyKey, any>) => (
            <ColorDomainCellWithLink
                metricName={BenchmarkMetricName.trafficShare}
                value={value}
                row={row}
            />
        ),
    },
    audienceReach: {
        ...defaultPercentageConfig,
        width: 160,
        key: "audienceReach",
        dataIndex: "audienceReach",
        displayName: translate("bt.report.metric.audienceReach.table.header.title.audienceReach"),
        title: translate("bt.report.metric.audienceReach.table.header.title.audienceReach"),
        align: "right",
    },
    trafficShare: {
        ...defaultPercentageConfig,
        key: "trafficShare",
        dataIndex: "trafficShare",
        displayName: translate("activation.home.widget.body.table.column.traffic"),
        title: translate("activation.home.widget.body.table.column.traffic"),
        align: "right",
    },
    audienceOverlap: {
        ...defaultPercentageConfig,
        key: "audienceOverlap",
        dataIndex: "n/a",
        displayName: translate("bt.report.metric.share.view.header.title.overlap.text"),
        title: translate("bt.report.metric.share.view.header.title.overlap.text"),
    },
    mom: {
        ...defaultConfig,
        key: "pop",
        dataIndex: "pop",
        displayName: translate("common.mom"),
        title: translate("common.mom"),
        render: (value: number, row: Record<PropertyKey, any>) => (
            <ChangeCell
                value={row?.isOtherCompetitors ? undefined : value}
                isInverted={row?.isInverted}
                formattedValue={formatChangeValue(value)}
            />
        ),
        align: "right",
    },
    wow: {
        ...defaultConfig,
        key: "wow",
        dataIndex: "pop",
        displayName: translate("common.wow"),
        title: translate("common.wow"),
        render: (value: number, row: Record<PropertyKey, any>) => (
            <ChangeCell
                value={row?.isOtherCompetitors ? undefined : value}
                isInverted={row?.isInverted}
                formattedValue={formatChangeValue(value)}
            />
        ),
        align: "right",
    },
    yoy: {
        ...defaultConfig,
        key: "yoy",
        dataIndex: "yoy",
        displayName: translate("common.yoy"),
        title: translate("common.yoy"),
        render: (value: number, row: Record<PropertyKey, any>) => (
            <ChangeCell
                value={row?.isOtherCompetitors ? undefined : value}
                isInverted={row?.isInverted}
                formattedValue={formatChangeValue(value)}
            />
        ),
        align: "right",
    },
    trends: {
        ...defaultConfig,
        key: "trends",
        dataIndex: "trends",
        width: 120,
        displayName: translate("bt.report.metric.share.view.header.title.trend.text", { monthCount: 3 }),
        title: translate("bt.report.metric.share.view.header.title.trend.text", { monthCount: 3 }),
        render: (value: string[], row: Record<PropertyKey, any>) =>
            value ? (
                <TrendCell
                    value={value}
                    row={row}
                    Container={TrendContainer}
                />
            ) : (
                <BaseCell className="default-empty-centered">-</BaseCell>
            ),
        align: "right",
    },
    rank: {
        ...defaultConfig,
        key: "rank",
        dataIndex: "",
        width: 56,
        displayName: translate("common.rank"),
        title: translate("common.rank"),
        render: (_: number, row: Record<PropertyKey, any>) => (
            <StyledCellContainer>
                {RANK_SYMBOL}
                <BaseCell>{row?.rank}</BaseCell>
            </StyledCellContainer>
        ),
        resize: false,
    },
    rankChange: {
        ...defaultConfig,
        key: "rankChange",
        dataIndex: "rankChange",
        width: 60,
        displayName: translate("activation.home.widget.body.table.column.rank.change"),
        title: translate("activation.home.widget.body.table.column.rank.change"),
        align: "right",
        render: (value: number) => <ChangeRankCell value={value} />,
    },
});

const getParentColumns = (): Record<ParentColumnNames, Partial<BenchmarkColumnSchema>> => ({
    currentPeriod: {
        key: "currentPeriod",
        title: translate("bt.report.metric.share.view.header.title.currentPeriod"),
        align: "center",
    },
    weekly: {
        key: "weekly",
        title: translate("bt.report.metric.share.view.header.title.week.over.week"),
    },
    monthly: {
        key: "monthly",
        title: translate("bt.report.metric.share.view.header.title.month.over.month"),
    },
    yearly: {
        key: "yearly",
        title: translate("bt.report.metric.share.view.header.title.year.over.year"),
    },
});

export const generateColumns = (
    columns: Array<ColumnNames | BenchmarkColumnSchema>,
    overrides: Record<string, Partial<BenchmarkColumnSchema>> = {},
): BenchmarkColumnSchema[] =>
    columns.map((column) => {
        if (typeof column === "string") {
            const columnConfig = {
                ...getDefaultColumns()[column],
                ...overrides[column],
            };

            return columnConfig as BenchmarkColumnSchema;
        }

        return column;
    });

export const generateColumnWithChildren = (
    parent: ParentColumnNames,
    childrenNames: ColumnNames[],
    childrenOverrides: Record<string, Partial<BenchmarkColumnSchema>> = {},
): BenchmarkColumnSchema => {
    const parentColumn = getParentColumns()[parent];
    const childrenColumns = childrenNames.map((child) => ({
        ...getDefaultColumns()[child],
        ...childrenOverrides[child],
    }));

    if (!parentColumn) {
        return {} as BenchmarkColumnSchema;
    }

    return {
        ...parentColumn,
        children: childrenColumns,
    } as BenchmarkColumnSchema;
};
